import { useEffect, useMemo, useState } from 'react';
import { Form, Input, InputNumber, Switch, Row, Col } from 'antd';
import { Button, PageHeader } from 'components';
import { CheckOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'hooks/useNavigate';
import axios from 'axios';
import useShoppingCenter from '../useShoppingCenter';
import styles from './style.module.scss';

const RIMAIL_API_URL = process.env.REACT_APP_RIMAIL_API_URL;

const SourceForm = ({ isNew = false }) => {
  const { id, sourceId } = useParams();
  const navigate = useNavigate();
  const { singleShop } = useShoppingCenter(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    const fetchSource = async () => {
      if (isNew) return;
      if (
        !singleShop?.rimail_agent_id ||
        !singleShop?.rimail_token ||
        !sourceId
      )
        return;
      setLoading(true);
      try {
        const response = await axios.get(
          `${RIMAIL_API_URL}/chatgpt_agents/${singleShop.rimail_agent_id}/chatgpt_agent_sources/${sourceId}`,
          {
            headers: {
              'auth-token': singleShop.rimail_token,
            },
          },
        );
        const source =
          response?.data?.chatgpt_agent_source || response?.data || {};
        form.setFieldsValue({
          source_name: source.source_name || '',
          source_url: source.source_url || '',
          crawl_depth: source.crawl_depth,
          follow_external: !!source.follow_external,
          max_pages: source.max_pages,
          content_char_limit: source.content_char_limit,
          request_timeout: source.request_timeout,
          use_js: !!source.use_js,
          load_more_text: source.load_more_text || '',
          load_more_max_clicks: source.load_more_max_clicks,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchSource();
  }, [
    isNew,
    sourceId,
    singleShop?.rimail_agent_id,
    singleShop?.rimail_token,
    form,
  ]);

  const payloadFromValues = (values) => ({
    chatgpt_agent_source: {
      source_name: values.source_name || '',
      source_url: values.source_url || '',
      crawl_depth: values.crawl_depth,
      follow_external: !!values.follow_external,
      max_pages: values.max_pages,
      content_char_limit: values.content_char_limit,
      use_js: !!values.use_js,
      request_timeout: values.request_timeout,
      load_more_max_clicks: values.load_more_max_clicks,
      load_more_text: values.load_more_text || '',
    },
  });

  const onFinish = async (values) => {
    if (!singleShop?.rimail_agent_id || !singleShop?.rimail_token) return;
    setSubmitLoading(true);
    try {
      const urlBase = `${RIMAIL_API_URL}/chatgpt_agents/${singleShop.rimail_agent_id}/chatgpt_agent_sources`;
      if (isNew) {
        await axios.post(urlBase, payloadFromValues(values), {
          headers: {
            'auth-token': singleShop.rimail_token,
          },
        });
      } else {
        await axios.patch(`${urlBase}/${sourceId}`, payloadFromValues(values), {
          headers: {
            'auth-token': singleShop.rimail_token,
          },
        });
      }
      navigate(`/admin/agents/${id}`);
    } finally {
      setSubmitLoading(false);
    }
  };

  const title = useMemo(
    () => (isNew ? 'Nuova sorgente agente' : 'Modifica sorgente agente'),
    [isNew],
  );

  return (
    <div>
      <PageHeader title={title} />
      <div className={styles.content}>
        <Form
          form={form}
          layout='vertical'
          onFinish={onFinish}
          autoComplete='off'
          disabled={loading}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label='Nome sorgente'
                name='source_name'
                rules={[{ required: true, message: 'Obbligatorio' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label='URL sorgente'
                name='source_url'
                rules={[{ required: true, message: 'Obbligatorio' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label='Profondità crawl'
                name='crawl_depth'
                extra='Valori da 1 a 5'
              >
                <InputNumber min={1} max={5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label='Segui link esterni'
                name='follow_external'
                valuePropName='checked'
                extra='Seguire i collegamenti esterni'
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label='Numero massimo di pagine'
                name='max_pages'
                extra='Massimo di pagine da analizzare (predefinito 100)'
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label='Limite caratteri contenuto'
                name='content_char_limit'
                extra='Massimo di caratteri estratti per pagina (predefinito 10000)'
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label='Timeout richiesta (secondi)'
                name='request_timeout'
                extra='Timeout richieste HTTP/JS in secondi (predefinito 10)'
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label='Browser headless (JS)'
                name='use_js'
                valuePropName='checked'
                extra='Abilitare per siti SPA/React con contenuto renderizzato via JS'
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label='Testo pulsante “carica altro”'
                name='load_more_text'
                extra='Testo del pulsante per la paginazione (es. “Carica altro”, “Load more”)'
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label='Clic massimi su “carica altro”'
                name='load_more_max_clicks'
                extra='Numero massimo di clic sul pulsante (predefinito 50)'
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col>
              <Button
                onClick={() => form.submit()}
                icon={<CheckOutlined />}
                loading={submitLoading}
                width={'150px'}
                big
              >
                Salva
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  );
};

export default SourceForm;
